import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LocalizedValueModel, ManagedHttpContext } from '@osos/shared/types';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ConfigService } from '../../config';
import { ToastService, TypesUtilsService } from '../../services';
import { isJsonString } from '../../utils';

@Injectable()
export class GeneralErrorInterceptorInterceptor implements HttpInterceptor {
  constructor(
    private toastService: ToastService,
    private typesUtilsService: TypesUtilsService,
    private translateService: TranslateService,
    private router: Router,
    private configService: ConfigService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<any> {
    if (!this._isApiRequest(request.url)) {
      return next.handle(request);
    }
    const httpContext = ManagedHttpContext.create(request.context);

    return next.handle(request).pipe(
      switchMap((response: any) => {
        if (
          !response?.body?.success &&
          !!response?.body?.maintenance?.enabled &&
          !!response?.body?.maintenance?.redirectTo
        ) {
          this.router.navigate([response.body.maintenance.redirectTo]);
          return EMPTY;
        }
        return of(response);
      }),
      catchError((error: unknown) => {
        if (httpContext.bypassErrorMiddleware) {
          throw error;
        }
        const castedError = error as HttpErrorResponse;
        const details: {
          fieldName: string;
          reason: LocalizedValueModel;
        }[] = castedError.error?.result?.details ?? [];
        const erro =
          typeof castedError?.error == 'string' &&
          isJsonString(castedError?.error)
            ? JSON.parse(castedError?.error)
            : castedError?.error;
        const message = erro?.error?.message;
        const errorDetails = erro?.result?.details;

        if (
          castedError?.status === 400 ||
          castedError?.status === 422 ||
          castedError?.status === 404
        ) {
          if (erro?.error?.code === 'CAN_NOT_ARCHIVE_TEMPLATE_IN_USE') {
            const translationKey = 'message.' + erro.error.code;
            this.toastService.errorMsg(
              this.translateService.instant(translationKey, {
                reactions: erro.result.details.reactions.join(', '),
              }),
            );
          } else if (erro?.error?.code === 'CAN_NOT_ARCHIVE_DOMAIN_IN_USE') {
            const translationKey = 'message.' + erro.error.code;
            this.toastService.errorMsg(
              this.translateService.instant(translationKey, {
                usedInside: erro.result.details.usedInside.join(', '),
              }),
            );
          } else if (
            !message?.en &&
            !message?.ar &&
            erro?.error?.code &&
            !this._isEntriesApi(request.url)
          ) {
            const translationKey = 'message.' + erro?.error?.code;
            this.toastService.errorMsg(
              this.translateService.instant(translationKey),
            );
          } else if (message) {
            if (
              erro?.error?.code === 'ALL_ENTRY_VALIDATION_ERROR' &&
              details?.length &&
              this._isEntriesApi(request.url)
            ) {
              details.forEach((detail) => {
                this.toastService.errorMsg(
                  this.typesUtilsService.getLocaleString(
                    detail.reason,
                    errorDetails,
                  ),
                );
              });
            } else {
              this.toastService.errorMsg(
                this.typesUtilsService.getLocaleString(message, errorDetails),
              );
            }
          }

          throw error;
        }

        if (castedError?.status === 409) {
          const message = this.typesUtilsService.getLocaleString(
            castedError?.error?.error?.message ?? '',
            errorDetails,
          );
          this.toastService.errorMsg(`${castedError?.status} ${message}`);
        }

        if (castedError?.status === 403) {
          throw error;
        }
        return null;
      }),
    );
  }

  private _isApiRequest(url: string): boolean {
    return this.configService
      .getApiBaseUrls()
      .some((baseUrl) => url.startsWith(baseUrl));
  }
  private _isEntriesApi(url: string) {
    return url.includes('entries');
  }
}
